import { inject } from "@angular/core";
import { CanActivateFn, Router } from "@angular/router";
import { map, take } from "rxjs";
import { CognitoUtilsService } from "../../auth/services/cognito-utils.service";
import {
  Permission,
  hasPermission,
  getPagePermission,
} from "../config/permissions.config";
import { MODULES_CONFIG } from "../config/modules.config";

/**
 * Guard to check if user has a specific permission
 * Redirects to first accessible module if user doesn't have permission
 *
 * Usage:
 * ```typescript
 * {
 *   path: 'schedule',
 *   canActivate: [hasPermissionGuard('page:planning:schedule')],
 *   component: ScheduleComponent
 * }
 * ```
 */
export function hasPermissionGuard(permission: Permission): CanActivateFn {
  return () => {
    const cognitoUtils = inject(CognitoUtilsService);
    const router = inject(Router);

    // Roles come from the Cognito token (same source as the backend guard), not
    // the Mongo record, so the frontend and API agree on access.
    return cognitoUtils.getUserRoles().pipe(
      take(1),
      map((roles) => {
        // Check if user has the required permission
        if (hasPermission(roles, permission)) {
          return true;
        }

        // User doesn't have permission, redirect to root
        // The MainLayoutComponent will handle navigation to first accessible module
        return router.parseUrl("/");
      }),
    );
  };
}

/**
 * Guard that automatically extracts module and page from the current route
 * and checks the corresponding page permission
 * Redirects to root if user doesn't have permission
 *
 * Usage:
 * ```typescript
 * {
 *   path: 'projects',
 *   canActivate: [autoPagePermissionGuard],  // Automatically checks 'page:planning:projects'
 *   component: ProjectsComponent
 * }
 * ```
 *
 * Note: Requires route structure: /{module}/{page}
 * For example: /planning/projects → page:planning:projects
 */
export const autoPagePermissionGuard: CanActivateFn = (_route, state) => {
  const cognitoUtils = inject(CognitoUtilsService);
  const router = inject(Router);

  // Extract module and page from URL
  const urlSegments = state.url.split("/").filter((s) => s);

  if (urlSegments.length < 2) {
    // Not enough segments for page permission check
    return router.parseUrl("/");
  }

  const moduleId = urlSegments[0];
  const pageId = urlSegments[1];
  const permission: Permission = `page:${moduleId}:${pageId}` as Permission;

  return cognitoUtils.getUserRoles().pipe(
    take(1),
    map((roles) => {
      if (hasPermission(roles, permission)) {
        return true;
      }

      return router.parseUrl("/");
    }),
  );
};

/**
 * Guard that redirects to the first accessible page within a module.
 * Used as default route handler instead of hardcoded redirectTo.
 *
 * This solves the problem where a user has access to a module but not
 * to the default page (e.g., PM can access analytics:sal but not analytics:margin).
 *
 * Usage:
 * ```typescript
 * {
 *   path: "",
 *   canActivate: [moduleDefaultRedirectGuard("analytics")],
 *   children: []
 * }
 * ```
 *
 * @param moduleId - The module ID to find accessible pages in
 * @returns CanActivateFn that redirects to first accessible page
 */
export function moduleDefaultRedirectGuard(moduleId: string): CanActivateFn {
  return () => {
    const cognitoUtils = inject(CognitoUtilsService);
    const router = inject(Router);

    return cognitoUtils.getUserRoles().pipe(
      take(1),
      map((roles) => {
        // Find the module configuration
        const moduleConfig = MODULES_CONFIG.find((m) => m.id === moduleId);
        if (!moduleConfig) {
          return router.parseUrl("/");
        }

        // Find the first page the user has access to
        const accessiblePage = moduleConfig.pages.find((page) => {
          const permission = getPagePermission(moduleId, page.id);
          return hasPermission(roles, permission);
        });

        if (accessiblePage) {
          // Redirect to the first accessible page
          return router.parseUrl(`/${moduleId}/${accessiblePage.path}`);
        }

        // No accessible pages in this module, redirect to root
        return router.parseUrl("/");
      }),
    );
  };
}
